import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { UserPreferences } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';

const QK = ['user', 'preferences'];

export function useUserPreferences() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery<UserPreferences, Error>({
    queryKey: QK,
    queryFn: () => getSDK().user.getPreferences(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  return useMutation<UserPreferences, Error, Partial<UserPreferences>, { prev?: UserPreferences }>({
    mutationFn: (patch) => getSDK().user.updatePreferences(patch),
    onMutate: async (patch) => {
      const prev = queryClient.getQueryData<UserPreferences>(QK);
      queryClient.setQueryData<UserPreferences>(QK, { ...(prev ?? {}), ...patch });
      await queryClient.cancelQueries({ queryKey: QK });
      return { prev };
    },
    onError: (_e, _patch, ctx) => {
      if (ctx?.prev !== undefined) queryClient.setQueryData(QK, ctx.prev);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<UserPreferences>(QK, data);
    },
    onSettled: () => { void queryClient.invalidateQueries({ queryKey: QK }); },
  });
}

/**
 * The `editor_autosave_enabled` preference for the workspace file editor.
 * Default is TRUE when unset — only an
 * explicit `false` disables the idle autosave. Rides the same per-user
 * preferences surface as the chat panel layout (no new tables).
 */
export function useEditorAutosavePreference(): {
  autosaveEnabled: boolean;
  setAutosaveEnabled: (enabled: boolean) => void;
  isLoading: boolean;
} {
  const { data, isLoading } = useUserPreferences();
  const mutation = useUpdatePreferences();
  return {
    autosaveEnabled: data?.editor_autosave_enabled !== false,
    setAutosaveEnabled: (enabled) => mutation.mutate({ editor_autosave_enabled: enabled }),
    isLoading,
  };
}
